import {
  Box,
  Button,
  Chip,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  DialogTitle,
  DialogContent,
  Card,
  Grid,
  DialogActions,
  IconButton,
  Checkbox
} from '@mui/material';
import {
  Attachment,
  Note,
  PurchaseOrderPartial,
  PurchaseOrderWithRelations,
  useGetPurchaseOrdersQuery,
  useLazyGetPurchaseOrdersQuery,
  useLazyGetProcurementProcessesQuery,
  usePatchPurchaseOrdersByIdMutation,
} from '../../../redux/vmsApi';
import React, { useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as XLSX from 'xlsx';
import config from "../../../config";
import { getCurrentDate } from "../../../utils/dateUtil";
import { formatCurrency } from '../../../utils/currencyUtil';
import { useRouter } from '../../../hooks/useRouter';
import LoadingCover from 'components/common/LoadingCover';
import DropzoneArea from 'components/common/DropzoneArea';
import { useAppSelector } from 'redux/hooks';

const PurchaseOrderList: React.FC = () => {
  const router = useRouter();

  const [triggerFetch] = useLazyGetProcurementProcessesQuery();

  const [filters, setFilters] = useState({
    amount: '',
    supplierName: '',
    itemCode: '',
    purchaseOrderNumber: '',
    fromDate: '2025-01-06',
    toDate: getCurrentDate(),
    development: '',
    project: '',
    reqNumber: '',
    title: '',
    detailed: false,
    backOrder: false,
    reportByEmail: false,
    reportName: '',
    currency: '',
    latestAction: '',
    status: ''
  });
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleViewProcessDetails = (procurementProcessId: string | undefined) => {
    if (!procurementProcessId) return;
    window.open(`/procurement/procurementProcess/${procurementProcessId}`, '_blank');
  };

  const [filteredActions, setFilteredActions] = useState<string[] | null>(null);
  const user = useAppSelector(state => state.auth.user);
  const [isClicked, setIsClicked] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isTaxClearance, setIsTaxClearance] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isAddNotesOpen, setIsAddNotesOpen] = useState(false);
  const [patchPurchaseOrdersById] = usePatchPurchaseOrdersByIdMutation();
  const [triggerPurchaseOrdersExport] = useLazyGetPurchaseOrdersQuery();
  const [selectedAction, setSelectedAction] = useState<any | null>('Notify');
  const [isExporting, setIsExporting] = useState(false);

  const actions = [
    'Request Cancellation',
    'Request Closure',
    'Cancel',
    'Close',
    'Approve',
    'Notify'
  ];

  const userRoles = user?.roles || [];

  const handleCheckboxChange = (action: string) => {
    setSelectedAction((prev: string) => (prev === action ? null : action));
  };

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });
  const [reportByEmailEnabled, setReportByEmailEnabled] = useState(false);

  const { data: purchaseOrders, isLoading, isError } = useGetPurchaseOrdersQuery(apiParams);


  const handleAddNotes = (selectedPurchaseOrder: PurchaseOrderPartial) => {
    if (selectedPurchaseOrder?.notes)
      setNotes(selectedPurchaseOrder?.notes);
    const filteredActions = actions.filter((action) => {

      if (['Request Cancellation', 'Request Closure'].includes(action)) {
        if (!(userRoles.includes('procurement-clerk') || userRoles.includes('admin'))) {
          return false;
        }
      }

      if (['Approve'].includes(action)) {
        if (!(userRoles.includes('cpo') || userRoles.includes('admin'))) {
          return false;
        }
      }

      if (['Cancel', 'Close'].includes(action)) {
        if (!(userRoles.includes('pro-chair') || userRoles.includes('admin'))) {
          return false;
        }
      }

     
      // Ensure that the PRO-Chair only sees 'Cancel' if the clerk requested cancellation
      // and only sees 'Close' if the clerk requested closure.
      const request = selectedPurchaseOrder?.statusModificationRequest?.requestedAction;

      if (action === 'Cancel' && request !== 'Request Cancellation') {
        return false; 
      }

      if (action === 'Close' && request !== 'Request Closure') {
        return false;
      }

      return true;
    });
    setFilteredActions(filteredActions);
    setIsAddNotesOpen(true);
  };

  const handleAddNotesDialogClose = () => {
    setIsAddNotesOpen(false);
    setIsClicked(false);
    setNewComment('');
    setNotes(null);
  };
  const handleFilterChange = (field: string, value: string | boolean) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setApiParams({
      ...filters,
      skip: 0,
      limit: pageSize,
    });
    setPage(0);
    setReportByEmailEnabled(false);
    handleFilterChange('reportName', '');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prevParams) => ({
      ...prevParams,
      skip: newPage * pageSize,
    }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prevParams) => ({
      ...prevParams,
      limit: newSize,
      skip: 0,
    }));
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const exportData = await triggerPurchaseOrdersExport({
        ...apiParams,
        reportByEmail: false,
        reportName: '',
        skip: 0,
        limit: 200000,
      }).unwrap();

      const rows = (exportData ?? []).map((purchaseOrder: PurchaseOrderWithRelations) => ({
        poNumber: purchaseOrder.purchaseOrderNumber ?? '',
        reqNumber: purchaseOrder.reqNumber ?? '',
        title: purchaseOrder.title ?? '',
        development: purchaseOrder.development ?? '',
        project: purchaseOrder.project ?? '',
        supplier: purchaseOrder.quotation?.supplier?.name ?? '',
        date: purchaseOrder.date ? new Date(purchaseOrder.date).toISOString() : '',
        status: purchaseOrder.status ?? '',
        paymentStatus: purchaseOrder.paymentStatus ?? '',
        currency: purchaseOrder.quotation?.currency ?? '',
        purchaseOrderValue: purchaseOrder.quotation?.grandTotal ?? '',
        deliveredValue: purchaseOrder.fullFilledValue ?? '',
        paidValue: purchaseOrder.paid ?? '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'PurchaseOrders');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      XLSX.writeFile(workbook, `purchase-orders-${timestamp}.xlsx`);
    } catch (error) {
      console.error('Failed to export purchase orders Excel', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenNotes = async (attachment: any) => {
    try {
      const fileName = attachment.name;
      if (!fileName) {
        console.error("No document attached");
      }
      setOpenDialog(`${config.CDN_URL}/procurement/${fileName}`);
    }

    catch (e) {
      console.error("Error opening document dialog", e);
      setOpenDialog(null);
    }
  }


  const handleDialogOpen = async (
    selectedPurchaseOrder: PurchaseOrderPartial,
    documentType: 'purchaseOrder' | 'requisition' | 'quotation'
  ) => {
    const procurementProcessId = selectedPurchaseOrder.procurementProcessId;
    try {
      const result = await triggerFetch({ procurementProcessId });

      const processes = result.data;
      if (!processes || processes.length === 0) {
        console.error("Procurement process not found");
      } else {
        const process = processes[0];

        const fileName =
          documentType === "purchaseOrder"
            ? process.purchaseOrderDocument?.name
            : documentType === "quotation"
              ? selectedPurchaseOrder?.quotation?.attachments?.[0].name
              : process.requisitionDocument?.name;

        if (!fileName) {
          console.error("No document attached");
        }
        setOpenDialog(`${config.CDN_URL}/procurement/${fileName}`);
      }

    } catch (e) {
      console.error("Error opening document dialog", e);
      setOpenDialog(null);
    }
  };

  const handleDialogClose = () => setOpenDialog(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrderWithRelations | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>, purchaseOrder: PurchaseOrderWithRelations) => {
    setAnchorEl(event.currentTarget);
    setSelectedPurchaseOrder(purchaseOrder);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubmit = async () => {

    setIsClicked(true);
    setShowOverlay(true);
    if (selectedAction && newComment && user)
      try {
        const newNote: Note = {
          comment: newComment,
          action: {
            user,
            createdAt: getCurrentDate(),
            requestedAction: selectedAction
          },
          attachments,
          isTaxClearance
        };

        
        if (selectedPurchaseOrder && selectedPurchaseOrder.id)
          await patchPurchaseOrdersById({
            id: selectedPurchaseOrder.id,
            purchaseOrderPartial: {
              notes: [...(notes ?? []), newNote],
              latestAction: newNote.action,
            },

          }).unwrap();

        setAttachments([]);
        handleAddNotesDialogClose();
      } catch (error) {

      }

    setShowOverlay(false);
  };


  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error fetching purchase orders.</div>;
  }

  return (
    <React.Fragment>
      <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: '#f9f9f9', boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
          Purchase Orders
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Title"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.title}
            onChange={(e) => handleFilterChange('title', e.target.value)}
          />

          <TextField
            label="Supplier Name"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.supplierName}
            onChange={(e) => handleFilterChange('supplierName', e.target.value)}
          />


          <TextField
            label="Purchase Order Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.purchaseOrderNumber}
            onChange={(e) => handleFilterChange('purchaseOrderNumber', e.target.value)}
          />
          <TextField
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
          />
          <TextField
            label="To Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
          />

          <TextField
            label="Requisition Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.reqNumber}
            onChange={(e) => handleFilterChange('reqNumber', e.target.value)}
          />
          <TextField
            label="Development"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.development}
            onChange={(e) => handleFilterChange('development', e.target.value)}
          />

          <TextField
            label="Project"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.project}
            onChange={(e) => handleFilterChange('project', e.target.value)}
          />

          <TextField
            label="Item Code"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.itemCode}
            onChange={(e) => handleFilterChange('itemCode', e.target.value)}
          />
          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="currency-select-label">Currency</InputLabel>
            <Select
              labelId="currency-select-label"
              value={filters.currency || ''}
              label="Currency"
              onChange={(e) => handleFilterChange('currency', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ZAR">ZAR</MenuItem>
              <MenuItem value="ZIG">ZIG</MenuItem>
            </Select>
          </FormControl>


          <TextField
            label="Purchase Order Value"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.amount}
            onChange={e => handleFilterChange('amount', e.target.value)}
          />

          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={filters.status || ''}
              label="status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Partially Delivered">Partially Delivered</MenuItem>
              <MenuItem value="Fully Delivered">Fully Delivered</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="latestAction-select-label">Action Required </InputLabel>
            <Select
              labelId="latestAction-select-label"
              value={filters.latestAction || ''}
              label="Action Required"
              onChange={(e) => handleFilterChange('latestAction', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Request Cancellation">Cancellation Requests</MenuItem>
              <MenuItem value="Request Closure">Closure Requests</MenuItem>
              <MenuItem value="Approve">Approved Requests</MenuItem>
              <MenuItem value="Notify">Notifications</MenuItem>
              <MenuItem value="Close">Closed</MenuItem>
              <MenuItem value="Cancel">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={reportByEmailEnabled}
                onChange={(e: { target: { checked: any; }; }) => {
                  const isEnabled = e.target.checked;
                  setReportByEmailEnabled(isEnabled);
                  handleFilterChange('reportByEmail', isEnabled);
                  if (!isEnabled) {
                    handleFilterChange('reportName', '');
                  }
                }}
                color="primary"
              />
            }
            label="Generate & Email Report"
            sx={{ marginLeft: 1 }}
          />

          {reportByEmailEnabled && (
            <TextField
              label="Report Name"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
              value={filters.reportName}
              onChange={(e) => handleFilterChange('reportName', e.target.value)}
            />
          )}


        </Box>
        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1565c0',
              },
            }}
          >
            Apply Filters
          </Button>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={isExporting}
            onClick={handleExportExcel}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            {isExporting ? 'Exporting...' : 'Export Excel (All Filtered)'}
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PO Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>REQ Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Purchase Order Value</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Delivered/Paid</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseOrders && purchaseOrders.length > 0 ? (
              purchaseOrders.map((purchaseOrder: PurchaseOrderWithRelations, index: number) => (
                <TableRow key={purchaseOrder.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.purchaseOrderNumber || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.reqNumber || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.title || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.development || 'N/A'}, {purchaseOrder.project || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.quotation.supplier.name || 'N/A'}
                  </TableCell>

                  <TableCell>
                    {new Date(purchaseOrder?.date).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}{' '}
                  </TableCell>

                  <TableCell component="th" scope="row">
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignItems: 'center',
                      }}
                    >
                      <Chip
                        label={purchaseOrder.status || 'N/A'}
                        sx={{
                          backgroundColor:
                            purchaseOrder.status === 'Fully Delivered'
                              ? '#388e3c'
                              : purchaseOrder.status === 'Partially Delivered'
                                ? '#fbc02d'
                                : purchaseOrder.status === 'Approved'
                                  ? '#1976d2'
                                  : purchaseOrder.status === 'Cancelled'
                                    ? '#d32f2f'
                                    : '#9e9e9e',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                        size="small"
                      />

                      <Chip
                        label={purchaseOrder.paymentStatus || 'N/A'}
                        sx={{
                          backgroundColor:
                            purchaseOrder.paymentStatus === 'Not Yet Paid'
                              ? '#d32f2f'
                              : purchaseOrder.paymentStatus === 'Partially Paid'
                                ? '#fbc02d'
                                : purchaseOrder.paymentStatus === 'Fully Paid'
                                  ? '#388e3c'
                                  : '#9e9e9e',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                        size="small"
                      />
                    </Box>

                    {(purchaseOrder.statusModificationRequest || purchaseOrder.statusModified) && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          marginLeft: 1,
                        }}
                      >
                        {purchaseOrder.statusModificationRequest && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic', fontSize: '0.75rem', color: '#555' }}
                          >
                            Action Requested By: {purchaseOrder.statusModificationRequest.user.name}
                          </Typography>
                        )}
                        <br></br>
                        {purchaseOrder.statusModified && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic', fontSize: '0.75rem', color: '#555' }}
                          >
                            Action Completed By: {purchaseOrder.statusModified.user.name}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell component="th" scope="row">
                    {formatCurrency(purchaseOrder.quotation.grandTotal, purchaseOrder.quotation.currency || 'usd')}
                    {purchaseOrder.quotation.paymentUpfrontPercentage != null && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${purchaseOrder.quotation.paymentUpfrontPercentage}% Payment Upfront`}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}
                  </TableCell>

                  <TableCell component="th" scope="row">
                    {formatCurrency(purchaseOrder.fullFilledValue, purchaseOrder.quotation.currency || 'usd') || 'N/A'}/ {formatCurrency(purchaseOrder.paid, purchaseOrder.quotation.currency) || 'N/A'}
                  </TableCell>



                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => handleOpen(e, purchaseOrder)}
                    >
                      Actions
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          elevation: 3,
                          sx: {
                            minWidth: 200,
                            borderRadius: 1,
                          },
                        }
                      }}
                    >
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrder?.procurementProcessId) {
                          handleDialogOpen(selectedPurchaseOrder, 'purchaseOrder');
                        }
                      }}>
                        View Purchase Order Document
                      </MenuItem>

                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrder) {
                          handleDialogOpen(selectedPurchaseOrder, 'quotation');
                        }
                      }}>
                        View Quotation Document
                      </MenuItem>

                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrder) {
                          handleDialogOpen(selectedPurchaseOrder, 'requisition');
                        }
                      }}>
                        View Requisition Document
                      </MenuItem>

                      {selectedPurchaseOrder?.procurementProcessId && (
                        <MenuItem onClick={() => {
                          handleClose();
                          handleViewProcessDetails(selectedPurchaseOrder.procurementProcessId);
                        }}>
                          View Process Details
                        </MenuItem>
                      )}

                      {selectedPurchaseOrder?.purchaseOrderNumber && (
                        <MenuItem onClick={() => {
                          handleClose();
                          router.push(`/rtp/new-from-po?po=${encodeURIComponent(selectedPurchaseOrder.purchaseOrderNumber)}`);
                        }}>
                          Create RTP From This PO
                        </MenuItem>
                      )}

                      {selectedPurchaseOrder?.procurementProcessId && (
                        <MenuItem onClick={() => {
                          handleClose();
                          handleAddNotes(selectedPurchaseOrder);
                        }}>
                          Add/View notes
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>

                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">No matching Purchase Orders found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          disabled={page === 0}
          variant="contained"
          sx={{ marginRight: 2 }}
        >
          Previous
        </Button>

        <Typography variant="body1">Page {page + 1}</Typography>

        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={purchaseOrders?.length === undefined || purchaseOrders.length < pageSize}
          variant="contained"
          sx={{ marginLeft: 2 }}
        >
          Next
        </Button>
      </Box>


      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ backgroundColor: '#f0f4f7' }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <Box p={2}>
          {openDialog && (
            <iframe
              key={openDialog || "empty"}
              src={openDialog || ""}

              title="Document Viewer"
              width="100%"
              height="500px"
              style={{ border: 'none' }}
            />
          )}
          <Box textAlign="right" mt={2}>
            <Button onClick={handleDialogClose} variant="contained" color="primary">
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={isAddNotesOpen} onClose={handleAddNotesDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Add Notes
        </DialogTitle>
        <DialogContent>
          {showOverlay && <LoadingCover />}
          <Card
            elevation={3}
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)',
              borderLeft: '4px solid #1976d2',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 24px 0 rgba(0,0,0,0.15)'
              }
            }}
          >

            <Typography variant="h6" gutterBottom>Existing Notes</Typography>
            {notes && notes.length === 0 && (
              <Typography variant="body2" color="textSecondary">No notes yet</Typography>
            )}

            {notes && notes.map((note, idx) => (
              <Card key={idx} sx={{ mb: 2, p: 2 }}>
                <Typography variant="body1">{note.comment}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {note.action.user?.name} • {new Date(note.action.createdAt ?? "").toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </Typography>

                {notes && note.attachments && note?.attachments?.length > 0 && (
                  <ul>
                    {note.attachments.map((att, i) => (
                      <li key={i}>
                        <a target="_blank" rel="noopener noreferrer">{att.name}</a>
                        <IconButton onClick={() => handleOpenNotes(att)} sx={{ color: '#1976d2' }}>
                          <VisibilityIcon />
                        </IconButton>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}

          </Card>



          <Grid item xs={12}>

            <TextField
              label="Comment"
              multiline
              fullWidth
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
              Attachments
            </Typography>
            <DropzoneArea
              setAttachments={setAttachments}
              attachments={attachments}
              fieldName='documents'
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                Actions
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {filteredActions && filteredActions.map((action) => (
                  <FormControlLabel
                    key={action}
                    control={
                      <Checkbox
                        checked={selectedAction === action}
                        onChange={() => handleCheckboxChange(action)}
                        disabled={selectedAction !== null && selectedAction !== action}
                      />
                    }
                    label={action}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddNotesDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isClicked}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default PurchaseOrderList;